import type React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import styled from 'styled-components';
import { PAGE_SIZE } from '@constants';
import { BlockLoading } from '@components/BlockLoading';
import Empty from './Empty';
import type { ItemListRowProps } from './types';

interface Props<T> {
  emptyString?: string;
  hasNextPage?: boolean;
  itemCount: number;
  itemData?: ItemListRowProps<T>['data'];
  itemHeight?: number;
  loadMoreItems?: (startIndex: number, stopIndex: number) => Promise<void>;
  loading?: boolean;
  pageSize?: number;
  noEmptyImage?: boolean;
  row: React.ComponentType<ItemListRowProps<T>>;
}

const List = styled(FixedSizeList)`
  &::-webkit-scrollbar {
    background-color: transparent;
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    border: 0;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }
`;

function ItemList<T>({
  emptyString = '暂无内容',
  hasNextPage = true,
  itemCount,
  itemData,
  itemHeight = 50,
  loadMoreItems,
  loading,
  pageSize = PAGE_SIZE,
  noEmptyImage = false,
  row,
}: Props<T>) {
  if (loading) return <BlockLoading />;
  if (!itemCount) return <Empty text={emptyString} noImg={noEmptyImage} />;

  if (loadMoreItems) {
    return (
      <div className="absolute inset-0">
        <InfiniteLoader
          isItemLoaded={(index) => !hasNextPage || index < itemCount}
          itemCount={hasNextPage ? itemCount + 1 : itemCount}
          loadMoreItems={loadMoreItems}
          minimumBatchSize={pageSize}
          threshold={10}
        >
          {({ onItemsRendered, ref }) => (
            <AutoSizer>
              {({ width, height }) => (
                <List
                  height={height}
                  itemData={itemData}
                  itemCount={itemCount}
                  itemSize={itemHeight}
                  width={width}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                >
                  {row}
                </List>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
    );
  }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <List
          height={height}
          itemData={itemData}
          itemCount={itemCount}
          itemSize={itemHeight}
          width={width}
        >
          {row}
        </List>
      )}
    </AutoSizer>
  );
}

export default ItemList;
